export type LocalCartSyncInput = {
  // The resolved user is a guest checkout placeholder (not a registered account)
  isGuestCheckoutUser: boolean;
  // The better-auth session is recognised client-side (false for any guest session)
  isAuthenticated: boolean;
  // A server basket already exists for this user
  hasServerBasket: boolean;
  // Number of items currently displayed in the cart panel
  localItemsCount: number;
};

/**
 * A guest's cart items live in localStorage until they are pushed to the
 * server basket. When the guest already exists (returning guest) but the
 * session is not recognised client-side, the server basket may still be empty
 * even though a basket id exists: any server-side action on the basket
 * (discount code, checkout) must sync the local items first.
 */
export const shouldSyncLocalCartToServer = ({
  isGuestCheckoutUser,
  isAuthenticated,
  hasServerBasket,
  localItemsCount,
}: LocalCartSyncInput): boolean =>
  isGuestCheckoutUser && localItemsCount > 0 && (!hasServerBasket || !isAuthenticated);

export const DISCOUNT_CODE_EMAIL_LOCK_MESSAGE =
  "Retirez le code promo pour modifier votre email.";
export const GROUP_TICKET_EMAIL_LOCK_MESSAGE =
  "Retirez les billets de votre groupe pour modifier votre email.";

type CartItemWithUserType = {
  quantity: number;
  buyableItem: {
    ticketType?: { userType: string | null } | null;
  };
};

/**
 * A Group ticket (ADR 0005) is only eligible for the email that unlocked it: as long
 * as one sits in the cart, the guest email that opened the session must not change.
 */
export const hasGroupTicketInCart = (items: CartItemWithUserType[]): boolean =>
  items.some((item) => item.quantity > 0 && item.buyableItem.ticketType?.userType === "group");

/**
 * Why the guest email field is locked at checkout, or null when it is editable. A
 * promo code is bound to the server basket of that email, so its message wins.
 */
export const getGuestEmailLockMessage = ({
  hasAppliedDiscount,
  hasGroupTicket,
}: {
  hasAppliedDiscount: boolean;
  hasGroupTicket: boolean;
}): string | null => {
  if (hasAppliedDiscount) return DISCOUNT_CODE_EMAIL_LOCK_MESSAGE;
  if (hasGroupTicket) return GROUP_TICKET_EMAIL_LOCK_MESSAGE;
  return null;
};
